import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { RichText } from "@/components/RichText";
import { PenLine, Handshake, Award, ArrowRight, Mail } from "lucide-react";

const icons = { editorial: PenLine, trusted: Handshake, experience: Award };

export default function About() {
  const c = useContent();

  const team = [1, 2, 3, 4].map((n) => ({
    name: c[`about_team${n}_name`],
    role: c[`about_team${n}_role`],
    img: c[`about_team${n}_img`],
  }));

  const sections = [
    { id: "editorial", title: c.about_editorial_title, body: c.about_editorial_body, icon: icons.editorial },
    { id: "trusted-partners", title: c.about_trusted_title, body: c.about_trusted_body, icon: icons.trusted },
    { id: "experience", title: c.about_experience_title, body: c.about_experience_body, icon: icons.experience },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero with background */}
      <div className="relative overflow-hidden bg-[#2a0a12] text-white">
        <img src={c.about_bg_image_url} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: (Number(c.about_bg_opacity) || 0) / 100 }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a0a12]/90 via-[#2a0a12]/70 to-[#2a0a12]/45" />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <h1 className="font-serif text-4xl lg:text-5xl font-bold">{c.about_title}</h1>
          <p className="mt-5 text-slate-200 max-w-4xl leading-relaxed text-justify">{c.about_intro}</p>
        </div>
      </div>

      {/* Team — on top, square (uncropped) */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059] font-semibold">Our People</span>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-slate-900 mt-2">Meet the Team</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((m) => (
              <div key={m.name} className="text-center group" data-testid={`team-${(m.name || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-lg bg-slate-50 aspect-square flex items-center justify-center">
                  <img src={m.img} alt={m.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="font-serif text-xl text-slate-900 mt-5">{m.name}</h3>
                <p className="text-sm text-[#800020] font-medium mt-1">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sections with highlighted headings */}
      <div className="max-w-5xl mx-auto px-6 py-14 space-y-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] font-semibold">{sections.map((s) => s.title).join(" | ")}</div>
        {sections.map((s, idx) => {
          const Icon = s.icon;
          return (
            <section key={s.id} data-testid={`about-${s.id}`} className={idx > 0 ? "pt-12 border-t border-slate-200" : ""}>
              <div className="flex items-center gap-4 mb-5">
                <span className="h-12 w-12 rounded-lg bg-[#800020] flex items-center justify-center shrink-0"><Icon className="h-6 w-6 text-white" /></span>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#800020] uppercase tracking-wide">{s.title}</h2>
              </div>
              <div className="space-y-4 border-l-4 border-[#C5A059] pl-6">
                <RichText html={s.body} className="[&_p]:text-slate-700" />
              </div>
            </section>
          );
        })}
      </div>

      {/* List CTA */}
      <div className="max-w-5xl mx-auto px-6 pb-14">
        <div className="rounded-xl bg-[#2a0a12] text-white p-8 lg:p-10 text-center">
          <h2 className="font-serif text-3xl">List Your Boarding School</h2>
          <p className="mt-3 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            To list your Boarding School please fill-in the Listing Questionnaire, or just send us an email on{" "}
            <a href={`mailto:${c.contact_email}`} className="text-[#C5A059] underline">{c.contact_email}</a>.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 justify-center">
            <Link to="/list-your-school/listing-questionnaire" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] hover:bg-[#b48f46] text-[#2a0a12] font-semibold transition-colors" data-testid="about-questionnaire-cta">
              Listing Questionnaire <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={`mailto:${c.contact_email}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors font-medium" data-testid="about-email-cta">
              <Mail className="h-4 w-4" /> Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
